import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'

import { totmContentBaseUrl } from 'config'
import { types as classroomTypes } from 'actions/classroom'
import { authKeys } from 'utils/config'

const appId = process.env.REACT_APP_LOGROCKET_APP_ID

const REDACTED = '[redacted]'
const MAX_REDACTION_DEPTH = 12

// Keys are compared with punctuation and casing removed, so a single entry covers
// display_name, displayName and DisplayName.
const normalizeKey = (key) =>
  String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

// Redacted wherever they appear. These either describe a child specifically, or are
// credentials that nothing needs to see.
const ALWAYS_REDACTED_KEYS = new Set([
  'displayname',
  'child',
  'children',
  'childid',
  'child2id',
  'student',
  'students',
  'studentid',
  'studentids',
  'reader',
  'readers',
  'dob',
  'birthdate',
  'age',
  'gender',
  'password',
  'token',
  'accesstoken',
  'idtoken',
  'refreshtoken',
  'authorization',
  'apikey',
])

// Redacted unless they sit inside a teacher object, where they describe the adult
// running the session rather than a child. On their own there is no way to tell
// whose identity they hold, so the safe reading wins.
const AMBIGUOUS_IDENTITY_KEYS = new Set([
  'name',
  'firstname',
  'lastname',
  'fullname',
  'middlename',
  'givenname',
  'familyname',
  'username',
  'preferredusername',
  'id',
  'ids',
  'userid',
  'user',
  'email',
  'emailverified',
  'phone',
  'phonenumber',
  'address',
  'zip',
  'zipcode',
  'postalcode',
  'school',
  'schoolid',
  'district',
  'districtid',
  // `classroom` itself is the name of a store slice, not a field. What matters
  // inside it is the roster, and `students` is redacted unconditionally.
  'classroomid',
  'picture',
  'photo',
  'avatar',
  'sub',
])

// Descending through one of these marks the subtree as teacher data, which is
// recorded on purpose so a session can be traced to the teacher running it.
const TEACHER_KEYS = new Set(['teacher', 'teachers', 'teacherid', 'nickname'])

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-auth-token',
  'x-api-key',
])

const hostOf = (url) => {
  try {
    return new URL(url, window.location.origin).hostname.toLowerCase()
  } catch (e) {
    return ''
  }
}

const matchesHost = (host, candidates) =>
  Boolean(host) &&
  candidates.some(
    (candidate) => host === candidate || host.endsWith(`.${candidate}`)
  )

// Book art, audio and reading content. Public material with no classroom data in it.
const PII_FREE_HOSTS = [hostOf(totmContentBaseUrl)].filter(Boolean)

// Auth0 exchanges carry tokens and profile claims, and ResponsiveVoice is handed
// student names to read aloud. Neither is worth recording at all.
const DROPPED_HOSTS = [
  String(authKeys.domain || '').toLowerCase(),
  'auth0.com',
  'responsivevoice.org',
].filter(Boolean)

const redactValue = (value, depth = 0, inTeacher = false) => {
  if (value === null || typeof value !== 'object') return value
  if (depth >= MAX_REDACTION_DEPTH) return REDACTED
  if (Array.isArray(value))
    return value.map((entry) => redactValue(entry, depth + 1, inTeacher))

  return Object.keys(value).reduce((acc, key) => {
    const normalized = normalizeKey(key)

    if (ALWAYS_REDACTED_KEYS.has(normalized)) {
      acc[key] = REDACTED
    } else if (AMBIGUOUS_IDENTITY_KEYS.has(normalized) && !inTeacher) {
      acc[key] = REDACTED
    } else {
      acc[key] = redactValue(
        value[key],
        depth + 1,
        inTeacher || TEACHER_KEYS.has(normalized)
      )
    }

    return acc
  }, {})
}

const redactBody = (body) => {
  if (!body) return body
  if (typeof body === 'object') return redactValue(body)
  if (typeof body !== 'string') return body

  try {
    return JSON.stringify(redactValue(JSON.parse(body)))
  } catch (e) {
    // Individual fields can't be located in a non-JSON body, so drop all of it.
    return REDACTED
  }
}

const redactHeaders = (headers) => {
  if (!headers || typeof headers !== 'object') return headers

  return Object.keys(headers).reduce((acc, key) => {
    acc[key] = SENSITIVE_HEADERS.has(key.toLowerCase())
      ? REDACTED
      : headers[key]
    return acc
  }, {})
}

export const requestSanitizer = (request) => {
  const host = hostOf(request.url)
  if (matchesHost(host, DROPPED_HOSTS)) return null

  const headers = redactHeaders(request.headers)
  if (matchesHost(host, PII_FREE_HOSTS)) return { ...request, headers }

  return { ...request, headers, body: redactBody(request.body) }
}

export const responseSanitizer = (response) => {
  const headers = redactHeaders(response.headers)

  // response.url is not guaranteed, so an unknown origin falls through to redaction.
  if (response.url && matchesHost(hostOf(response.url), PII_FREE_HOSTS)) {
    return { ...response, headers }
  }

  return { ...response, headers, body: redactBody(response.body) }
}

// This payload is the roster and nothing else, so it is dropped without inspecting
// its shape. That keeps new API fields from leaking before anyone thinks to add them
// to STUDENT_PII_KEYS. SET_TEACHER is intentionally not here.
const ROSTER_ACTIONS = new Set([classroomTypes.SET_STUDENTS])

export const actionSanitizer = (action) => {
  if (!action) return action
  if (ROSTER_ACTIONS.has(action.type)) {
    return { ...action, payload: REDACTED }
  }
  // The payload arrives as a bare { id } with no `teacher` key to recognize it by,
  // so the subtree has to be marked as teacher data explicitly.
  if (action.type === classroomTypes.SET_TEACHER) {
    return { ...action, payload: redactValue(action.payload, 0, true) }
  }
  return redactValue(action)
}

export const stateSanitizer = (state) => redactValue(state)

export const isLogRocketEnabled = Boolean(appId)

// Tags the session with the teacher running it. Auth0's `sub` is the identity key
// because it is stable and available immediately; the backend teacher id arrives
// later with the roster and is attached as a trait so it can be cross-referenced
// against the teacherID recorded on session events.
export const identifyTeacher = ({ user, teacherId } = {}) => {
  if (!isLogRocketEnabled || !user?.sub) return

  const traits = {}
  if (user.nickname) traits.nickname = user.nickname
  if (user.email) traits.email = user.email
  if (user.name) traits.name = user.name
  if (teacherId) traits.teacherId = String(teacherId)

  LogRocket.identify(user.sub, traits)
}

export const logRocketMiddleware = () =>
  LogRocket.reduxMiddleware({ actionSanitizer, stateSanitizer })

export const initLogRocket = () => {
  if (!isLogRocketEnabled) return

  LogRocket.init(appId, {
    dom: {
      inputSanitizer: true,
    },
    network: {
      requestSanitizer,
      responseSanitizer,
    },
    console: {
      // redux-logger prints the full store, students included, outside production.
      isEnabled: { log: false, debug: false },
    },
  })

  setupLogRocketReact(LogRocket)
}

export default LogRocket
